import React, { useEffect, useRef, useState } from "react"
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { getPlaceDetailsByPlaceId } from "../../../../utils/placeDetails"
import { DatePickerInput, TimePickerInput, DateTimeProvider } from "../../../../components/DateTime"
import { useMapsReady } from "../../../../hooks/useMapsReady"
import { getDefaultOptions } from "../../../../utils/defaultCityOptions"
import { saveRecentSearch } from "../../../../utils/recentSearches"

const formatShortDate = (dateStr) => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
}

const formatShortTime = (timeStr) => {
  if (!timeStr) return null
  const [h, m] = timeStr.split(":").map(Number)
  if (isNaN(h)) return timeStr
  const ampm = h >= 12 ? "PM" : "AM"
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`
}

const truncate = (str, n = 18) => (str && str.length > n ? str.slice(0, n) + "…" : str)

const panelPlacesStyles = {
  container: (p) => ({ ...p, width: "100%" }),
  control: (provided) => ({
    ...provided,
    border: "1.5px solid #e0e0e0",
    boxShadow: "none",
    width: "100%",
    height: "44px",
    minHeight: "44px",
    background: "#fff",
    fontSize: "1.3rem",
    color: "#333",
    borderRadius: "0.6rem",
  }),
  valueContainer: (p) => ({ ...p, height: "44px", padding: "0 10px" }),
  input: (p) => ({ ...p, color: "#333", fontSize: "1.3rem", margin: 0, padding: 0 }),
  placeholder: (p) => ({ ...p, color: "#aaa", fontSize: "1.3rem" }),
  singleValue: (p) => ({ ...p, color: "#333" }),
  menu: (p) => ({ ...p, fontSize: "1.3rem", zIndex: 99999 }),
}

const UpdateRentalForm = () => {
  const [formValues, setFormValues] = useState({
    location: null,
    package: "",
    pickUpDate: "",
    pickUpTime: "",
    mobileNumber: "",
  })

  const { location } = useSelector((state) => state.location)
  const navigate = useNavigate()
  const params = useParams()
  const [mobileExpanded, setMobileExpanded] = useState(false)
  const mapsReady = useMapsReady()
  const [defaultLocationOptions, setDefaultLocationOptions] = useState([])
  const [locationMenuIsOpen, setLocationMenuIsOpen] = useState(false)

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)

  const today = new Date()
  const formattedToday = today.toLocaleDateString("en-CA")

  const loadFormValues = () => {
    const storedTripDataStr = localStorage.getItem("tripData")
    if (!storedTripDataStr) return
    const storedTripData = JSON.parse(storedTripDataStr)

    const convertStringToPlaceFormat = (stringValue, lat, lng, city) => {
      if (!stringValue) return null
      if (typeof stringValue === "string") {
        return {
          label: stringValue,
          value: { description: stringValue, place_id: `temp_${Date.now()}_${Math.random()}` },
          latitude: lat || null,
          longitude: lng || null,
          city: city || "",
          fullAddress: stringValue,
        }
      } else if (stringValue?.label && stringValue?.value) {
        return stringValue
      }
      return null
    }

    setFormValues({
      location: storedTripData.pickup ? convertStringToPlaceFormat(storedTripData.pickup, storedTripData.pickup_lat, storedTripData.pickup_long, storedTripData.pickup_city) : null,
      package: storedTripData.package_type_in_hours || "",
      pickUpDate: storedTripData.trip_date || "",
      pickUpTime: storedTripData.trip_time || "",
      mobileNumber: storedTripData.phone_number || "",
    })
  }

  useEffect(() => {
    loadFormValues()
    const handleStorageChange = () => loadFormValues()
    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("tripDataUpdated", handleStorageChange)
    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("tripDataUpdated", handleStorageChange)
    }
  }, [params.fromCity, params.toCity])

  useEffect(() => {
    const loadDefaultOptions = async () => {
      const currentCity = location?.city || formValues?.location?.city || formValues?.location?.label
      setDefaultLocationOptions(await getDefaultOptions("from", currentCity) || [])
    }
    loadDefaultOptions()
  }, [location?.city, formValues?.location])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handlePlaceSelect = async (value) => {
    if (!value) { setFormValues((prev) => ({ ...prev, location: null })); return }
    try {
      const placeId = value?.value?.place_id || value?.place_id
      const details = placeId ? await getPlaceDetailsByPlaceId(placeId) : null
      if (details) {
        const nextValue = { ...value, latitude: details.lat, longitude: details.lng, city: details.city, fullAddress: details.fullAddress }
        setFormValues((prev) => ({ ...prev, location: nextValue }))
        saveRecentSearch(nextValue, "from")
        setTimeout(() => { pickUpDateRef.current?.open?.() }, 150)
      }
    } catch (error) {
      console.error("Error fetching location details:", error)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formValues.location) return toast.error("Please select location")
    if (!formValues.package) return toast.error("Please select package")
    if (!formValues.pickUpDate) return toast.error("Please select pickup date")
    if (!formValues.pickUpTime) return toast.error("Please select pickup time")

    const storedTrip = JSON.parse(localStorage.getItem("tripData") || "{}")
    const phoneFromUrl = new URLSearchParams(window.location.search).get("phone_number") || ""
    const getFullAddress = (v) => (v ? (typeof v === "string" ? v : v.fullAddress || v.label || "") : "")

    const tripData = {
      trip_type: 2,
      round_trip: 0,
      pickup: getFullAddress(formValues.location) || storedTrip.pickup || "",
      drop: "",
      pickup_lat: formValues.location?.latitude || storedTrip.pickup_lat || null,
      pickup_long: formValues.location?.longitude || storedTrip.pickup_long || null,
      drop_lat: null,
      drop_long: null,
      pickup_city: formValues.location?.city || storedTrip.pickup_city || "",
      drop_city: "",
      package_type_in_hours: parseInt(formValues.package, 10) || 0,
      trip_date: formValues.pickUpDate || "",
      trip_time: formValues.pickUpTime || "",
      phone_number: formValues.mobileNumber || storedTrip.phone_number || phoneFromUrl || "",
      ip_address: location?.ip || "8425",
    }

    localStorage.setItem("tripData", JSON.stringify(tripData))

    const buildParam = (key, value) => (value !== null && value !== undefined && value !== "" ? `${key}=${encodeURIComponent(value)}` : "")
    const queryParams = [
      `trip_type=${tripData.trip_type}`,
      `round_trip=${tripData.round_trip}`,
      buildParam("pickup", tripData.pickup),
      buildParam("pickup_lat", tripData.pickup_lat),
      buildParam("pickup_long", tripData.pickup_long),
      buildParam("pickup_city", tripData.pickup_city),
      buildParam("package_type_in_hours", tripData.package_type_in_hours),
      buildParam("trip_date", tripData.trip_date),
      buildParam("trip_time", tripData.trip_time),
      buildParam("phone_number", tripData.phone_number),
    ].filter(Boolean)

    navigate(`/cabs?${queryParams.join("&")}`)
  }

  const locationLabel = formValues.location?.label || "—"
  const packageLabel = formValues.package || "—"
  const pickupDateShort = formatShortDate(formValues.pickUpDate)
  const pickupTimeShort = formatShortTime(formValues.pickUpTime)
  const placesComponents = { DropdownIndicator: () => null, IndicatorSeparator: () => null, ClearIndicator: () => null }

  return (
    <div className="updateRentalForm">
      {/* ── BAR (always visible) ── */}
      <div className="updateRentalForm__bar">
        <span className="updateRentalForm__bar-badge">Rental</span>

        <div className="updateRentalForm__bar-chip">
          <span className="updateRentalForm__bar-chipLabel">Location</span>
          <span className="updateRentalForm__bar-chipValue">{truncate(locationLabel, 20)}</span>
        </div>

        <div className="updateRentalForm__bar-chip">
          <span className="updateRentalForm__bar-chipLabel">Package</span>
          <span className="updateRentalForm__bar-chipValue">{truncate(packageLabel, 16)}</span>
        </div>

        <div className="updateRentalForm__bar-chip">
          <span className="updateRentalForm__bar-chipLabel">Date</span>
          <span className="updateRentalForm__bar-chipValue">{pickupDateShort || "—"}</span>
        </div>

        <div className="updateRentalForm__bar-chip updateRentalForm__bar-chip--time">
          <span className="updateRentalForm__bar-chipLabel">Time</span>
          <span className="updateRentalForm__bar-chipValue">{pickupTimeShort || "—"}</span>
        </div>

        <button
          className="updateRentalForm__bar-modifyBtn"
          onClick={() => setMobileExpanded((v) => !v)}
          type="button"
        >
          {mobileExpanded ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Close
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Modify
            </>
          )}
        </button>
      </div>

      {/* ── PANEL (slides down on Modify) ── */}
      <div className={`updateRentalForm__panel${mobileExpanded ? " updateRentalForm__panel--open" : ""}`}>
        <form onSubmit={handleSubmit} className="updateRentalForm__container">
          <div className="updateRentalForm__field updateRentalForm__field--location">
            <label className="field-label">Location</label>
            {mapsReady && <GooglePlacesAutocomplete
              debounce={400}
              minLengthAutocomplete={3}
              selectProps={{
                value: formValues.location || null,
                onChange: (v) => { handlePlaceSelect(v); setLocationMenuIsOpen(false) },
                placeholder: "Location *",
                isClearable: true,
                defaultOptions: defaultLocationOptions,
                menuIsOpen: locationMenuIsOpen,
                onFocus: () => setLocationMenuIsOpen(true),
                onBlur: () => setLocationMenuIsOpen(false),
                onMenuClose: () => setLocationMenuIsOpen(false),
                getOptionLabel: (e) => (e && e.label) || "",
                getOptionValue: (e) => e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",
                components: placesComponents,
                styles: panelPlacesStyles,
              }}
              autocompletionRequest={{ componentRestrictions: { country: "in" } }}
            />}
          </div>

          <div className="updateRentalForm__field updateRentalForm__field--package">
            <label className="field-label">Package</label>
            <select className="updateRentalForm__select" name="package" value={formValues.package} onChange={handleChange} required>
              <option value="" disabled>Select Package *</option>
              <option value="4">4Hours - 40 km</option>
              <option value="6">6Hours - 60 km</option>
              <option value="8">8Hours - 80km</option>
              <option value="12">12Hours - 120km</option>
            </select>
          </div>

          <DateTimeProvider>
            <div className="updateRentalForm__field updateRentalForm__field--date dtp-white">
              <label className="field-label">Pickup Date</label>
              <DatePickerInput
                ref={pickUpDateRef}
                value={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
                minDate={new Date(formattedToday)}
                placeholder="Pick Up Date *"
                onChange={(d) => {
                  setFormValues((prev) => ({ ...prev, pickUpDate: d ? new Date(d).toLocaleDateString("en-CA") : "" }))
                  setTimeout(() => { pickUpTimeRef.current?.open?.() }, 200)
                }}
              />
            </div>
            <div className="updateRentalForm__field updateRentalForm__field--time ttp-white">
              <label className="field-label">Pickup Time</label>
              <TimePickerInput
                ref={pickUpTimeRef}
                value={formValues.pickUpTime}
                placeholder="Pick Up Time *"
                selectedDate={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
                onChange={(t) => setFormValues((prev) => ({ ...prev, pickUpTime: t || "" }))}
              />
            </div>
          </DateTimeProvider>

          <div className="updateRentalForm__field updateRentalForm__field--submit">
            <label className="field-label">&nbsp;</label>
            <button type="submit" className="updateRentalForm__submitBtn">Search</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UpdateRentalForm
